import { useFormContext, useWatch } from "react-hook-form";
import { ISalesInvoice } from "../../../types/Sales/salesInvoice";
import { useTranslation } from "react-i18next";
import { useDisable } from "../../../hooks/use-disable";
import { useEffect, useState } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { useStateContext } from "../../../contexts/auth-context";
import { useActivityLog } from "../../../hooks/use-activity-log";
import { useSnackbar } from "notistack";
import { useSalesOption } from "../../../hooks/Sales/use-sales-option";
import { IApprovalTemplate } from "../../../types/global";
import CustomizedButton from "../../../components/Custom/CustomizedButton";
import { useSalesError } from "../../../hooks/Sales/use-sales-error";
import { ActivityType } from "../../../generated/inventory";
import {
  ActivityLogDocumentType,
  SalesInvoiceCreateInput,
  SalesInvoiceFindUniqueQuery,
  SalesInvoiceUpdateInput,
  SalesReferenceDocumentType,
  useSalesInvoiceCreateMutation,
  useSalesInvoiceFindUniqueQuery,
  useSalesInvoiceUpdateMutation,
} from "../../../generated/sales";
import {
  IExternalProps,
  ISalesDeduction,
  ISalesPaymentChannel,
} from "../../../types/Sales";
import { createGraphQLClientWithMiddleware } from "../../../services/graphqlClient";
import { Box, CircularProgress, Stack } from "@mui/material";
import CustomerInfo from "../../../components/Form/Sales/CustomerInfo";
import AddressInfo from "../../../components/Form/Sales/CustomerInfo/AddressInfo";
import SalesInfo from "../../../components/Form/Sales/SalesInfo";
import SalesItemList from "../../../components/Table/Sales/ItemList/SalesItemList";
import SalesFooter from "../../../components/Form/Sales/Footer";
import BottomNavbar from "../../../components/UI/Navbar/BottomNavbar";
import SalesInvoiceHeader from "../../../components/Form/Sales/Invoice/Header";
import {
  salesInvoiceCreatePayloadFormatter,
  salesInvoiceUpdatePayloadFormatter,
} from "../../../utils/Formatter/Sales/SalesInvoice";
import dayjs from "dayjs";
import { errorMessageFormatter } from "../../../utils/Global";
import { validateSalesItemQty } from "../../../utils/Formatter/Sales";

type Props = {
  addPaymentHandler: (data: ISalesPaymentChannel) => void;
  addDeductionHandler: (data: ISalesDeduction) => void;
};

const DocumentInfoTab = ({ addPaymentHandler, addDeductionHandler }: Props) => {
  const {
    control,
    reset,
    handleSubmit,
    getValues,
    formState: { dirtyFields, errors },
  } = useFormContext<ISalesInvoice>();

  const status = useWatch({
    control,
    name: "aggrid_status",
  });

  const { t } = useTranslation();
  const [disabled, setDisabled] = useDisable();
  const [isEdit, setIsEdit] = useState<boolean>(false);
  const { id } = useParams();
  const { state } = useLocation();

  const {
    state: { authUser },
  } = useStateContext();
  const { createActivityLog } = useActivityLog();
  const { enqueueSnackbar } = useSnackbar();
  const navigate = useNavigate();
  const { isLoading: isOptionLoading, approvalTemplates } =
    useSalesOption("sales_invoice");
  const [approverList, setApproverList] = useState<IApprovalTemplate[]>([]);
  const [approvalStep, setApprovalStep] = useState<number>(0);
  const [isHaveApproval, setIsHaveApproval] = useState<boolean>(false);
  const isApproval = approvalTemplates && approvalTemplates.length > 0;
  const [netAmount, setNetAmount] = useState<number | null | undefined>(0);

  const graphQLClient = createGraphQLClientWithMiddleware("sales");

  const { data, isLoading, isSuccess, refetch } =
    useSalesInvoiceFindUniqueQuery<SalesInvoiceFindUniqueQuery>(
      graphQLClient,
      {
        uniqueInput: {
          id: id ? parseInt(id) : undefined,
        },
      },
      {
        enabled: !!id,
        cacheTime: 0,
      }
    );

  useEffect(() => {
    if (isSuccess) {
      const { SalesInvoiceFindUnique } = data;
      setNetAmount(SalesInvoiceFindUnique.net_amount);
    }
  }, [data, isSuccess]);

  useEffect(() => {
    if (isSuccess) {
      const { SalesInvoiceFindUnique } = data;
      const currentStep = SalesInvoiceFindUnique?.approval_step as number;
      setApprovalStep(currentStep);
      if (SalesInvoiceFindUnique?.main_status === "wait_approve") {
        setApproverList(approvalTemplates as IApprovalTemplate[]);
      } else {
        const allApproval = SalesInvoiceFindUnique?.approver_list || [];
        setApproverList(allApproval);
      }

      const currentReviewerList =
        approvalTemplates.find(
          (template) => template?.step_number === currentStep
        )?.approver_list || [];

      const isHaveApproval = currentReviewerList.some(
        (reviewer) => reviewer.id === authUser?.id
      );

      setIsHaveApproval(isHaveApproval);
    }
  }, [approvalTemplates, authUser?.id, data, isSuccess]);

  useEffect(() => {
    if (id && status !== "draft" && status !== "not_approved") {
      setDisabled(true);
    }

    return () => {
      setDisabled(false);
    };
  }, [id, setDisabled, status]);

  useEffect(() => {
    if (state) {
      const { copied_unique_id, copied_id, ...otherState } = state;
      reset({
        // ...salesInvoiceSchema,
        ...otherState,
        created_date: dayjs(),
        issue_date: dayjs(),
        due_date: dayjs().add(state.credit_day, "days"),
      });
    }
  }, [reset, state]);

  const { mutateAsync: create, isLoading: isCreating } =
    useSalesInvoiceCreateMutation<Error>(graphQLClient);

  const { mutateAsync: update, isLoading: isUpdating } =
    useSalesInvoiceUpdateMutation<Error>(graphQLClient);

  const draftHandler = async (data: ISalesInvoice) => {
    if (!id) {
      try {
        const formatData = salesInvoiceCreatePayloadFormatter(data, "draft");
        const { SalesInvoiceCreate } = await create({
          input: formatData as SalesInvoiceCreateInput,
        });
        enqueueSnackbar(`${t("button.save_draft")}สำเร็จ`, {
          variant: "success",
        });
        navigate(`/sales/invoice/${SalesInvoiceCreate.id}`);
        if (state && state.copied_id) {
          await createActivityLog({
            activity_type: ActivityType.Copy,
            document_type: ActivityLogDocumentType.SalesInvoice,
            reference_id: SalesInvoiceCreate?.id,
            activity_detail: {
              copied_from: {
                id: state.copied_id,
                unique_id: state.copied_unique_id,
              },
              copied_to: {
                id: SalesInvoiceCreate.id,
                unique_id: SalesInvoiceCreate.unique_id,
              },
            },
          });
        }
        await createActivityLog({
          activity_type: ActivityType.StatusChange,
          document_type: ActivityLogDocumentType.SalesInvoice,
          reference_id: SalesInvoiceCreate?.id,
          activity_detail: {
            secondary_operation: ActivityType.Create,
            curr_status: "draft",
            updated_fields: {
              net_amount: SalesInvoiceCreate.net_amount,
            },
          },
        });
      } catch (err) {
        const formatError = errorMessageFormatter(err);
        enqueueSnackbar(formatError || `${t("button.save_draft")}ไม่สำเร็จ`, {
          variant: "error",
        });
      }
    } else {
      try {
        const formatData = salesInvoiceUpdatePayloadFormatter(data, "draft");
        const { SalesInvoiceUpdate } = await update({
          uniqueInput: {
            id: id ? parseInt(id) : undefined,
          },
          input: formatData as SalesInvoiceUpdateInput,
        });
        enqueueSnackbar(`${t("button.save_draft")}สำเร็จ`, {
          variant: "success",
        });
        await refetch();

        const formattedDirtyFields = Object.keys(dirtyFields);

        if (formattedDirtyFields.length > 0) {
          await createActivityLog({
            activity_type: ActivityType.Edit,
            document_type: ActivityLogDocumentType.SalesInvoice,
            reference_id: SalesInvoiceUpdate?.id,
            activity_detail: {
              updated_fields: {
                net_amount:
                  netAmount?.toFixed(2) ===
                  SalesInvoiceUpdate.net_amount?.toFixed(2)
                    ? undefined
                    : SalesInvoiceUpdate.net_amount,
              },
            },
          });
        }
      } catch (err) {
        console.error(err);
        const formatError = errorMessageFormatter(err);
        enqueueSnackbar(formatError || `${t("button.save_draft")}ไม่สำเร็จ`, {
          variant: "error",
        });
      }
    }
  };

  const waitApproveHandler = async (data: ISalesInvoice) => {
    if (!id) {
      try {
        const formatData = salesInvoiceCreatePayloadFormatter(
          data,
          "wait_approve",
          isApproval ? 1 : 0
        );
        const { SalesInvoiceCreate } = await create({
          input: formatData as SalesInvoiceCreateInput,
        });
        enqueueSnackbar(`${t("button.send") + t("button.approve")}สำเร็จ`, {
          variant: "success",
        });
        navigate(`/sales/invoice/${SalesInvoiceCreate.id}`);
        if (state && state.copied_id) {
          await createActivityLog({
            activity_type: ActivityType.Copy,
            document_type: ActivityLogDocumentType.SalesInvoice,
            reference_id: SalesInvoiceCreate?.id,
            activity_detail: {
              copied_from: {
                id: state.copied_id,
                unique_id: state.copied_unique_id,
              },
              copied_to: {
                id: SalesInvoiceCreate.id,
                unique_id: SalesInvoiceCreate.unique_id,
              },
            },
          });
        }
        await createActivityLog({
          activity_type: ActivityType.StatusChange,
          document_type: ActivityLogDocumentType.SalesInvoice,
          reference_id: SalesInvoiceCreate?.id,
          activity_detail: {
            secondary_operation: ActivityType.Create,
            curr_status: "wait_approve",
            updated_fields: {
              net_amount: SalesInvoiceCreate.net_amount,
            },
          },
        });
      } catch (err) {
        console.error(err);
        const formatError = errorMessageFormatter(err);
        enqueueSnackbar(
          formatError || `${t("button.send") + t("button.approve")}ไม่สำเร็จ`,
          {
            variant: "error",
          }
        );
      }
    } else {
      try {
        const formatData = salesInvoiceUpdatePayloadFormatter(
          data,
          "wait_approve",
          isApproval ? 1 : 0
        );
        const { SalesInvoiceUpdate } = await update({
          uniqueInput: {
            id: id ? parseInt(id) : undefined,
          },
          input: formatData as SalesInvoiceUpdateInput,
        });
        await refetch();
        enqueueSnackbar(`${t("button.send") + t("button.approve")}สำเร็จ`, {
          variant: "success",
        });
        const formattedDirtyFields = Object.keys(dirtyFields);
        await createActivityLog({
          activity_type: ActivityType.StatusChange,
          document_type: ActivityLogDocumentType.SalesInvoice,
          reference_id: SalesInvoiceUpdate?.id,
          activity_detail: {
            secondary_operation:
              formattedDirtyFields?.length > 0 ? ActivityType.Edit : undefined,
            prev_status: data.aggrid_status,
            curr_status: "wait_approve",
            updated_fields: {
              net_amount:
                netAmount?.toFixed(2) ===
                SalesInvoiceUpdate.net_amount?.toFixed(2)
                  ? undefined
                  : SalesInvoiceUpdate.net_amount,
            },
          },
        });
      } catch (err) {
        const formatError = errorMessageFormatter(err);
        enqueueSnackbar(
          formatError || `${t("button.send") + t("button.approve")}ไม่สำเร็จ`,
          {
            variant: "error",
          }
        );
      }
    }
  };

  const approveHandler = async (data: ISalesInvoice) => {
    if (!id) {
      try {
        const reviewed_by = {
          user_id: authUser?.id,
          first_name: authUser?.first_name || "",
          last_name: authUser?.last_name || "",
          reviewed_date: dayjs(),
        };
        const formatData = salesInvoiceCreatePayloadFormatter(
          data,
          "wait_payment",
          undefined,
          reviewed_by
        );
        const { SalesInvoiceCreate } = await create({
          input: formatData as SalesInvoiceCreateInput,
        });
        enqueueSnackbar(`${t("button.approve")}สำเร็จ`, {
          variant: "success",
        });
        navigate(`/sales/invoice/${SalesInvoiceCreate.id}`);
        if (state && state.copied_id) {
          await createActivityLog({
            activity_type: ActivityType.Copy,
            document_type: ActivityLogDocumentType.SalesInvoice,
            reference_id: SalesInvoiceCreate?.id,
            activity_detail: {
              copied_from: {
                id: state.copied_id,
                unique_id: state.copied_unique_id,
              },
              copied_to: {
                id: SalesInvoiceCreate.id,
                unique_id: SalesInvoiceCreate.unique_id,
              },
            },
          });
        }
        await createActivityLog({
          activity_type: ActivityType.StatusChange,
          document_type: ActivityLogDocumentType.SalesInvoice,
          reference_id: SalesInvoiceCreate?.id,
          activity_detail: {
            secondary_operation: ActivityType.Create,
            curr_status: "wait_payment",
            updated_fields: {
              net_amount: SalesInvoiceCreate.net_amount,
            },
          },
        });
      } catch (err) {
        console.error(err);
        enqueueSnackbar(`${t("button.approve")}ไม่สำเร็จ`, {
          variant: "error",
        });
      }
    } else {
      try {
        let changingStatus = "wait_approve";
        let currentApprovalList: IApprovalTemplate[] = [];
        if (approvalStep && approvalStep > approvalTemplates.length - 1) {
          changingStatus = "wait_payment";
          currentApprovalList = approvalTemplates as IApprovalTemplate[];
        }

        if (!isApproval) {
          changingStatus = "wait_payment";
        }

        let currentApprovalStep = (approvalStep || 0) + 1;

        if (data.aggrid_status === "not_approved") {
          currentApprovalStep = approvalStep || 0;
        }

        const reviewed_by = {
          user_id: authUser?.id,
          first_name: authUser?.first_name || "",
          last_name: authUser?.last_name || "",
          reviewed_date: dayjs(),
        };

        const formatData = salesInvoiceUpdatePayloadFormatter(
          data,
          changingStatus,
          currentApprovalStep,
          currentApprovalList,
          false,
          reviewed_by
        );

        if (
          formatData.main_status === "wait_payment" &&
          formatData.reference_document_list &&
          formatData.reference_document_list.length > 0
        ) {
          const isValid = await validateSalesItemQty(
            formatData.item_list,
            SalesReferenceDocumentType.SalesInvoice
          );
          if (!isValid) {
            enqueueSnackbar("จำนวนสินค้าเกินจำนวนจากใบสั่งขาย", {
              variant: "error",
            });
            return;
          }
        }

        const { SalesInvoiceUpdate } = await update({
          uniqueInput: {
            id: id ? parseInt(id) : undefined,
          },
          input: formatData as SalesInvoiceUpdateInput,
        });
        enqueueSnackbar(`${t("button.approve")}สำเร็จ`, {
          variant: "success",
        });

        await refetch();

        if (isApproval) {
          if (changingStatus === "wait_payment") {
            await createActivityLog({
              activity_type: ActivityType.StatusChange,
              document_type: ActivityLogDocumentType.SalesInvoice,
              reference_id: SalesInvoiceUpdate?.id,
              activity_detail: {
                secondary_operation: ActivityType.Approve,
                message: [`ลำดับที่ ${approvalStep}`],
                prev_status: "wait_approve",
                curr_status: "wait_payment",
              },
            });
          } else {
            await createActivityLog({
              activity_type: ActivityType.Approve,
              document_type: ActivityLogDocumentType.SalesInvoice,
              reference_id: SalesInvoiceUpdate?.id,
              activity_detail: {
                message: [`ลำดับที่ ${approvalStep}`],
              },
            });
          }
        } else {
          const formattedDirtyFields = Object.keys(dirtyFields);
          await createActivityLog({
            activity_type: ActivityType.StatusChange,
            document_type: ActivityLogDocumentType.SalesInvoice,
            reference_id: SalesInvoiceUpdate?.id,
            activity_detail: {
              secondary_operation:
                formattedDirtyFields?.length > 0
                  ? ActivityType.Edit
                  : undefined,
              prev_status: data.aggrid_status,
              curr_status: "wait_payment",
              updated_fields:
                formattedDirtyFields?.length > 0
                  ? formattedDirtyFields
                  : undefined,
            },
          });
        }
      } catch (err) {
        console.error(err);
        enqueueSnackbar(`${t("button.approve")}ไม่สำเร็จ`, {
          variant: "error",
        });
      }
    }
  };

  const notApproveHandler = async (data: ISalesInvoice) => {
    try {
      const flag_status =
        data?.flag_status && data.flag_status.length > 0
          ? data.flag_status.includes("not_approved")
            ? [...data.flag_status]
            : [...data.flag_status, "not_approved"]
          : ["not_approved"];

      const { SalesInvoiceUpdate } = await update({
        input: {
          // item_price_list_id: data.item_price_list_id as number,
          currency: data.currency,
          price_vat_type: data.price_vat_type,
          flag_status: flag_status,
        } as SalesInvoiceUpdateInput,
        uniqueInput: {
          id: id ? parseInt(id) : undefined,
        },
      });

      await refetch();

      const formattedDirtyFields = Object.keys(dirtyFields);

      await createActivityLog({
        activity_type: ActivityType.StatusChange,
        document_type: ActivityLogDocumentType.SalesInvoice,
        reference_id: SalesInvoiceUpdate?.id,
        activity_detail: {
          secondary_operation:
            formattedDirtyFields?.length > 0 ? ActivityType.Edit : undefined,
          prev_status: data.aggrid_status,
          curr_status: "not_approved",
          updated_fields:
            formattedDirtyFields?.length > 0 ? formattedDirtyFields : undefined,
        },
      });

      enqueueSnackbar(`${t("button.not_approve")}สำเร็จ`, {
        variant: "success",
      });
    } catch (err) {
      console.error(err);
      enqueueSnackbar(`${t("button.not_approve")}ไม่สำเร็จ`, {
        variant: "error",
      });
    }
  };

  const editClickHandler = () => {
    setDisabled(false);
    setIsEdit(true);
  };

  const cancelEditHandler = () => {
    setIsEdit(false);
    setDisabled(true);
    reset();
  };

  const editHandler = async (data: ISalesInvoice, isNotApprove?: boolean) => {
    try {
      const formatData = salesInvoiceUpdatePayloadFormatter(
        data,
        data?.main_status || "",
        approvalStep,
        approverList,
        isNotApprove
      );
      const { SalesInvoiceUpdate } = await update({
        uniqueInput: {
          id: id ? parseInt(id) : undefined,
        },
        input: formatData as SalesInvoiceUpdateInput,
      });
      enqueueSnackbar(`${t("sentence.edit")}สำเร็จ`, {
        variant: "success",
      });
      await refetch();

      const formattedDirtyFields = Object.keys(dirtyFields);

      if (formattedDirtyFields.length > 0) {
        await createActivityLog({
          activity_type: ActivityType.Edit,
          document_type: ActivityLogDocumentType.SalesInvoice,
          reference_id: SalesInvoiceUpdate?.id,
          activity_detail: {
            secondary_operation:
              formattedDirtyFields?.length > 0 ? ActivityType.Edit : undefined,
            updated_fields: {
              net_amount:
                netAmount?.toFixed(2) ===
                SalesInvoiceUpdate.net_amount?.toFixed(2)
                  ? undefined
                  : SalesInvoiceUpdate.net_amount,
            },
          },
        });
      }
    } catch (err) {
      console.error(err);
      enqueueSnackbar(`${t("sentence.edit")}ไม่สำเร็จ`, {
        variant: "error",
      });
    }
  };

  const externalReferenceHandler = async (data: IExternalProps) => {
    try {
      const price_vat_type = getValues("price_vat_type");
      const currency = getValues("currency");
      await update({
        input: {
          external_reference_no: data.external_reference_no,
          main_status: "finished",
          sub_status: "finished",
          price_vat_type,
          currency,
        } as SalesInvoiceUpdateInput,
        uniqueInput: {
          id: id ? parseInt(id) : undefined,
        },
      });
      enqueueSnackbar(`${t("button.save")}สำเร็จ`, {
        variant: "success",
      });
      await refetch();
    } catch (err) {
      console.error(err);
      enqueueSnackbar(`${t("button.save")}ไม่สำเร็จ`, {
        variant: "error",
      });
    }
  };

  const cancelHandler = async () => {
    try {
      const data = getValues();
      const flag_status =
        data?.flag_status && data.flag_status.length > 0
          ? data.flag_status.includes("cancelled")
            ? [...data.flag_status]
            : [...data.flag_status, "cancelled"]
          : ["cancelled"];

      const { SalesInvoiceUpdate } = await update({
        uniqueInput: {
          id: id ? parseInt(id) : undefined,
        },
        input: {
          // item_price_list_id: data.item_price_list_id as number,
          currency: data.currency,
          price_vat_type: data.price_vat_type,
          flag_status: flag_status,
        },
      });

      enqueueSnackbar(`${t("button.cancel")}สำเร็จ`, {
        variant: "success",
      });

      await refetch();

      const formattedDirtyFields = Object.keys(dirtyFields);
      await createActivityLog({
        activity_type: ActivityType.StatusChange,
        document_type: ActivityLogDocumentType.SalesInvoice,
        reference_id: SalesInvoiceUpdate?.id,
        activity_detail: {
          secondary_operation:
            formattedDirtyFields?.length > 0 ? ActivityType.Edit : undefined,
          prev_status: data.aggrid_status,
          curr_status: "cancelled",
          updated_fields:
            formattedDirtyFields?.length > 0 ? formattedDirtyFields : undefined,
        },
      });
    } catch (err) {
      console.error(err);
      enqueueSnackbar(`${t("button.cancel")}ไม่สำเร็จ`, {
        variant: "error",
      });
    }
  };

  const renderButton = (status: string | undefined) => {
    switch (status) {
      case "draft":
        return (
          <>
            <CustomizedButton
              variant="outlined"
              title={t("button.save_draft")}
              disabled={isCreating || isUpdating}
              onClick={handleSubmit(draftHandler)}
            />
            {isApproval ? (
              <CustomizedButton
                title={t("button.send")}
                variant="contained"
                disabled={isCreating || isUpdating}
                onClick={handleSubmit(waitApproveHandler)}
              />
            ) : (
              <CustomizedButton
                title={t("button.approve")}
                variant="contained"
                disabled={isCreating || isUpdating}
                onClick={handleSubmit(approveHandler)}
              />
            )}
          </>
        );
      case "wait_approve":
        if (isEdit) {
          return (
            <>
              <CustomizedButton
                variant="outlined"
                title={t("button.cancel")}
                onClick={cancelEditHandler}
                disabled={isUpdating}
              />
              <CustomizedButton
                title={t("button.save")}
                variant="contained"
                onClick={() => {
                  handleSubmit((data) => editHandler(data))();
                  setIsEdit(false);
                  setDisabled(true);
                }}
                disabled={isUpdating}
              />
            </>
          );
        } else {
          if (isHaveApproval) {
            return (
              <>
                <CustomizedButton
                  variant="outlined"
                  title={t("button.not_approve")}
                  disabled={isUpdating}
                  onClick={handleSubmit(notApproveHandler)}
                />
                <CustomizedButton
                  title={t("button.approve")}
                  variant="contained"
                  onClick={handleSubmit(approveHandler)}
                  disabled={isUpdating}
                />
              </>
            );
          } else {
            return;
          }
        }
      case "not_approved":
        return (
          <>
            <CustomizedButton
              variant="outlined"
              title={t("button.save")}
              disabled={isUpdating}
              onClick={handleSubmit((data) => editHandler(data, true))}
            />
            <CustomizedButton
              title={t("button.send")}
              variant="contained"
              onClick={handleSubmit(waitApproveHandler)}
              disabled={isUpdating}
            />
          </>
        );
      case "wait_payment":
      case "partially_payment":
      case "fully_payment":
      case "cancelled":
      case "finished":
        if (isEdit) {
          return (
            <>
              <CustomizedButton
                variant="outlined"
                title={t("button.cancel")}
                disabled={isUpdating}
                onClick={cancelEditHandler}
              />
              <CustomizedButton
                title={t("button.save")}
                variant="contained"
                onClick={() => {
                  handleSubmit((data) => editHandler(data))();
                  setIsEdit(false);
                  setDisabled(true);
                }}
                disabled={isUpdating}
              />
            </>
          );
        }
        return;
      default:
        return (
          <>
            <CustomizedButton
              variant="outlined"
              title={t("button.save_draft")}
              disabled={isCreating || isUpdating}
              onClick={handleSubmit(draftHandler)}
            />
            {isApproval ? (
              <CustomizedButton
                title={t("button.send")}
                variant="contained"
                disabled={isCreating || isUpdating}
                onClick={handleSubmit(waitApproveHandler)}
              />
            ) : (
              <CustomizedButton
                title={t("button.approve")}
                variant="contained"
                disabled={isCreating || isUpdating}
                onClick={handleSubmit(approveHandler)}
              />
            )}
          </>
        );
    }
  };

  const isShowRemainingPayment = ["wait_payment", "partially_payment"].includes(
    status || ""
  );

  useSalesError(errors);

  if (isOptionLoading || (id && isLoading)) {
    return (
      <Box
        sx={{
          height: "calc(100dvh - 176px)",
          marginRight: "260px",
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  return (
    <>
      <SalesInvoiceHeader
        editClickHandler={editClickHandler}
        externalReferenceHandler={externalReferenceHandler}
        cancelHandler={cancelHandler}
        approverList={approverList as IApprovalTemplate[]}
        approvalStep={approvalStep}
        addPaymentHandler={addPaymentHandler}
        addDeductionHandler={addDeductionHandler}
        isEdit={isEdit}
      />
      <CustomerInfo />
      <AddressInfo />
      <SalesInfo />
      <SalesItemList documentType="sales_invoice" />
      {/* <Promotion /> */}
      <SalesFooter
        documentType="sales_invoice"
        isShowRemainingPayment={isShowRemainingPayment}
      />
      <BottomNavbar>
        {(!disabled || status === "wait_approve") && (
          <Stack direction="row" spacing={1} alignItems="center">
            {renderButton(status)}
          </Stack>
        )}
      </BottomNavbar>
    </>
  );
};

export default DocumentInfoTab;
